import { useEffect, useState } from "react";
import { Calculator, Loader2 } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { n1, pct, STATS, STAT_LABEL } from "@/lib/format";
import { Panel } from "@/components/AppShell";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export function OverUnder({ playerId, game, projection }) {
  const [stat, setStat] = useState("pts");
  const [line, setLine] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (projection) setLine(String(Math.floor(projection[stat].proj) + 0.5));
    setResult(null);
  }, [stat, projection]);

  const calc = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.get(`/players/${playerId}/over`, { params: { game_id: game.id, stat, line } });
      setResult(data);
    } catch (err) { setError(errorText(err)); }
    setBusy(false);
  };

  return (
    <Panel className="p-5 sm:p-6" data-testid="over-under">
      <h2 className="font-display text-2xl font-bold uppercase flex items-center gap-2"><Calculator className="h-5 w-5 text-emerald-400" /> Over / Under</h2>
      {!projection || !game ? <p className="text-sm text-slate-500 mt-3">Available when a projection exists.</p> : (
        <form onSubmit={calc} className="mt-4 space-y-4">
          <div className="flex flex-wrap gap-1">
            {STATS.map((s) => (
              <button type="button" key={s} data-testid={`ou-stat-${s}`} onClick={() => setStat(s)}
                className={`px-2.5 py-1 rounded text-xs font-bold transition-colors ${stat === s ? "bg-emerald-600 text-white" : "bg-slate-800 text-slate-400 hover:text-slate-100"}`}>{STAT_LABEL[s]}</button>
            ))}
          </div>
          <div className="flex gap-2">
            <Input data-testid="ou-line-input" type="number" step="0.5" min="0" value={line} onChange={(e) => setLine(e.target.value)} required className="bg-slate-900/70 font-mono-num" />
            <Button data-testid="ou-calc-btn" type="submit" disabled={busy} className="bg-emerald-600 hover:bg-emerald-500">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Calculate"}</Button>
          </div>
          <p className="text-xs text-slate-500 font-mono-num">Projection {n1(projection[stat].proj)} · sd {n1(projection[stat].sd)} · {stat === "fg3m" ? "Poisson" : "Normal"} model</p>
          {error && <p className="text-sm text-rose-400" data-testid="ou-error">{error}</p>}
          {result && (
            <div className="grid grid-cols-2 gap-3" data-testid="ou-result">
              {[["Over", result.p_over, result.fair_over, "ou-over"], ["Under", result.p_under, result.fair_under, "ou-under"]].map(([label, p, odds, tid]) => (
                <div key={label} data-testid={tid} className="rounded-md border border-slate-800 bg-slate-900/60 p-4">
                  <div className="text-xs uppercase tracking-widest text-slate-400">{label} {result.line}</div>
                  <div className="text-3xl font-extrabold font-mono-num mt-1">{pct(p)}</div>
                  <div className="mt-2 inline-block px-3 py-1 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-mono-num text-sm font-bold">
                    Fair {odds ?? "–"}
                  </div>
                </div>
              ))}
            </div>
          )}
          {result?.p_push > 0 && <p className="text-xs text-slate-500 font-mono-num" data-testid="ou-push">Push (exactly {result.line}): {pct(result.p_push)}</p>}
        </form>
      )}
    </Panel>
  );
}
